import { useId } from 'react';
import { addressShown, type DeliverTo as DeliverToValue } from '@/domain';
import { strings } from '@/shared/strings';
import { AddressBlock } from '@/ui/data/AddressBlock';
import { telHref } from '@/features/clients/contactLinks';
import styles from './DeliverTo.module.css';

export interface DeliverToProps {
  /**
   * What the case's payload carries: absent for a reader without seeIdentity,
   * null on a case that was never linked to a doctor's record.
   */
  deliverTo: DeliverToValue | null | undefined;
  /**
   * The doctor's name, as the first line of the label, where the screen does
   * not already say whose case it is.
   */
  name?: string | null;
  className?: string;
}

/**
 * Where a case is delivered: the doctor's clinic, address and number. The
 * request of 6 October 2026 (fifth), section 2: the address is shown in its
 * country's format everywhere a case is, so the case's own view and the
 * dispatch dialog both draw this, from what the case carries (`deliverTo`).
 *
 * Whether a reader sees it is the server's decision: `deliverTo` is sent only
 * to somebody who may see identity, and without it nothing is drawn. Nothing
 * for a case with no doctor's record behind it either.
 *
 * The address is the label where the doctor has one in parts, and otherwise
 * the text that was typed before addresses had parts, as it was typed:
 * `addressShown` in the domain makes that choice for every screen. The number
 * rings. A record that holds none of the three says so, since the desk about
 * to send a case out is the one that needs to know.
 */
export function DeliverTo({ deliverTo, name, className }: DeliverToProps) {
  const titleId = useId();
  if (deliverTo == null) return null;

  const address = addressShown(deliverTo);
  // There is no old number text on a case: the number is the international one or nothing.
  const dialled = deliverTo.phone ?? deliverTo.phoneDisplay;
  const shown = deliverTo.phoneDisplay ?? deliverTo.phone;
  const empty = deliverTo.clinic === null && dialled === null && address === null;

  return (
    <div
      className={className === undefined ? styles.block : `${styles.block} ${className}`}
      role="group"
      aria-labelledby={titleId}
    >
      <div id={titleId} className={styles.title}>
        {strings.deliverTo.title}
      </div>

      {empty ? (
        <p className={styles.none}>{strings.deliverTo.nothing}</p>
      ) : (
        <>
          <AddressBlock
            name={name}
            clinic={deliverTo.clinic}
            lines={address?.kind === 'lines' ? address.lines : []}
          />
          {address?.kind === 'text' && <p className={styles.text}>{address.text}</p>}
          {address === null && <p className={styles.none}>{strings.deliverTo.noAddress}</p>}
          {dialled !== null && shown !== null && (
            <a className={styles.phone} href={telHref(dialled)}>
              {shown}
            </a>
          )}
        </>
      )}
    </div>
  );
}
